import React, { useState, useCallback, useEffect } from 'react';
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Badge from '@mui/joy/Badge';
import Tooltip from '@mui/joy/Tooltip';
import { Question as HelpOutlineRounded, X as CloseRounded } from '@phosphor-icons/react';
import { useTickets } from '../../hooks/useSupport';
import { useActiveAIConversation } from '../../hooks/useSupport';
import type { AIChatMessage } from '../../types/support';
import SupportChatDrawer from './SupportChatDrawer';

const WELCOME_MESSAGE: AIChatMessage = {
  role: 'assistant',
  content: "Hi! I'm the ApexOS support assistant. I can help you with questions about any feature in the platform — from CRM and billing to marketing and scheduling. What can I help you with today?",
};

export default function SupportFloatingButton() {
  const [open, setOpen] = useState(false);
  const { data } = useTickets({ status: 'open' });
  const openCount = data?.count ?? 0;

  // Lifted chat state — survives drawer close and tab switch
  const [messages, setMessages] = useState<AIChatMessage[]>([WELCOME_MESSAGE]);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Load active conversation on mount
  const { data: activeConversation } = useActiveAIConversation();

  useEffect(() => {
    if (activeConversation?.messages && activeConversation.messages.length > 0) {
      setMessages([WELCOME_MESSAGE, ...activeConversation.messages]);
      setConversationId(activeConversation.id);
    }
  }, [activeConversation?.id]);

  const handleNewChat = useCallback(() => {
    setMessages([WELCOME_MESSAGE]);
    setConversationId(null);
  }, []);

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1299,
        }}
      >
        <Tooltip title={open ? 'Close support' : 'Get help'} placement="left">
          <Badge
            badgeContent={openCount || null}
            color="danger"
            size="sm"
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <IconButton
              variant="solid"
              onClick={() => setOpen(prev => !prev)}
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: '#7013f5',
                color: 'white',
                boxShadow: 'lg',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'scale(1.05)', boxShadow: 'xl', bgcolor: '#5c0fd4' },
              }}
            >
              {open ? (
                <CloseRounded size={24} />
              ) : (
                <HelpOutlineRounded size={24} />
              )}
            </IconButton>
          </Badge>
        </Tooltip>
      </Box>

      {open && (
        <SupportChatDrawer
          onClose={() => setOpen(false)}
          messages={messages}
          setMessages={setMessages}
          conversationId={conversationId}
          setConversationId={setConversationId}
          onNewChat={handleNewChat}
        />
      )}
    </>
  );
}
